// apps/web/src/components/painel/home/sparkline.tsx — Fase 17.8, Plano 03 (UI-SPEC U-08, U-23).
// Sparkline SVG própria (sem biblioteca de gráfico): 14 dias úteis, dia sem medição interrompe a linha (não vira zero),
// o último segmento (hoje, parcial) é tracejado e, com menos de 3 pontos medidos, o gráfico dá lugar a um texto.
// O desenho por máscara (clipPath com scaleX) só acontece na primeira carga e sem movimento reduzido.
import { useId } from 'react';
import type { ReactElement } from 'react';
import type { SerieDiaria } from '@pieltec/contracts';
import { usePrefersReducedMotion } from '../../../hooks/use-prefers-reduced-motion';
import { formatarNumero } from '../../../lib/painel/home/formato';
import '../../../styles/painel-movimento.css';

const LARGURA = 300;
const ALTURA = 44;
const MARGEM_X = 2;
const MARGEM_Y = 4;
const MIN_PONTOS_MEDIDOS = 3;
const CLASSE_LEGENDA = 'text-[length:var(--font-label)] font-normal leading-[1.4] text-muted-foreground';

export interface SparklineProps {
  readonly serie: SerieDiaria;
  /** Legenda da esquerda; padrão 'últimos 14 dias úteis'. */
  readonly rotulo?: string;
  /** Verdadeiro só na primeira carga: a linha se desenha por máscara. */
  readonly primeiraCarga: boolean;
}

interface Ponto {
  readonly x: number;
  readonly y: number;
}

const arredondar = (n: number): string => String(Math.round(n * 100) / 100);
const coordenada = (p: Ponto): string => `${arredondar(p.x)} ${arredondar(p.y)}`;

function formatarPonto(valor: number): string {
  return formatarNumero(valor, Number.isInteger(valor) ? 0 : 1);
}

/** Posições dos pontos medidos (null mantém o lugar vazio, sem virar zero). */
function posicionar(pontos: readonly (number | null)[]): readonly (Ponto | null)[] {
  const medidos = pontos.filter((p): p is number => p !== null);
  const minimo = Math.min(...medidos);
  const maximo = Math.max(...medidos);
  const intervalo = maximo - minimo;
  const passo = pontos.length > 1 ? (LARGURA - 2 * MARGEM_X) / (pontos.length - 1) : 0;
  return pontos.map((valor, indice) => {
    if (valor === null) return null;
    const fracao = intervalo === 0 ? 0.5 : (valor - minimo) / intervalo;
    return { x: MARGEM_X + indice * passo, y: ALTURA - MARGEM_Y - fracao * (ALTURA - 2 * MARGEM_Y) };
  });
}

/** Trecho sólido: cada sequência de segmentos sólidos vira um 'M ... L ...'; ponto isolado vira um ponto (traço redondo). */
function caminhoSolido(posicoes: readonly (Ponto | null)[], hojeParcial: boolean): string {
  const ultimo = posicoes.length - 1;
  const solido = (i: number): boolean => {
    const a = posicoes[i];
    const b = posicoes[i + 1];
    return a !== null && a !== undefined && b !== null && b !== undefined && !(hojeParcial && i === ultimo - 1);
  };
  const tocaTracejado = (i: number): boolean => hojeParcial && i >= ultimo - 1 && posicoes[ultimo - 1] != null && posicoes[ultimo] != null;
  const partes: string[] = [];
  posicoes.forEach((ponto, i) => {
    if (ponto === null) return;
    if (i > 0 && solido(i - 1)) partes.push(`L ${coordenada(ponto)}`);
    else if (i < ultimo && solido(i)) partes.push(`M ${coordenada(ponto)}`);
    else if (!tocaTracejado(i)) partes.push(`M ${coordenada(ponto)} L ${coordenada(ponto)}`);
  });
  return partes.join(' ');
}

/** Área sob a curva: uma por sequência contínua de pontos medidos (com 2 ou mais pontos). */
function caminhosDeArea(posicoes: readonly (Ponto | null)[]): readonly string[] {
  const areas: string[] = [];
  let corrente: Ponto[] = [];
  const fechar = (): void => {
    const primeiro = corrente[0];
    const ultimoPonto = corrente[corrente.length - 1];
    if (corrente.length >= 2 && primeiro !== undefined && ultimoPonto !== undefined) {
      areas.push(`M ${corrente.map(coordenada).join(' L ')} L ${arredondar(ultimoPonto.x)} ${ALTURA} L ${arredondar(primeiro.x)} ${ALTURA} Z`);
    }
    corrente = [];
  };
  for (const ponto of posicoes) {
    if (ponto === null) fechar();
    else corrente.push(ponto);
  }
  fechar();
  return areas;
}

export function Sparkline({ serie, rotulo, primeiraCarga }: SparklineProps): ReactElement {
  const reduzido = usePrefersReducedMotion();
  const idBase = useId().replace(/:/g, '');
  const idMascara = `${idBase}-mascara`;
  const idGradiente = `${idBase}-gradiente`;
  const { pontos, hojeParcial } = serie;
  const medidos = pontos.filter((p): p is number => p !== null);

  if (medidos.length < MIN_PONTOS_MEDIDOS) {
    return <p className={CLASSE_LEGENDA}>Histórico ainda curto para o gráfico.</p>;
  }

  const posicoes = posicionar(pontos);
  const ultimo = posicoes.length - 1;
  const penultimo = posicoes[ultimo - 1];
  const final = posicoes[ultimo];
  const tracejado = hojeParcial && penultimo != null && final != null ? `M ${coordenada(penultimo)} L ${coordenada(final)}` : null;
  const hoje = pontos[pontos.length - 1];
  const textoHoje = hoje === null || hoje === undefined ? 'sem medição' : formatarPonto(hoje);
  const descricao = `Últimos ${pontos.length} dias úteis: mínimo ${formatarPonto(Math.min(...medidos))}, máximo ${formatarPonto(Math.max(...medidos))}, hoje ${textoHoje}${hojeParcial ? ' (parcial)' : ''}.`;
  const desenhar = primeiraCarga && !reduzido;
  const estiloTraco = { stroke: 'var(--perfil-1)', vectorEffect: 'non-scaling-stroke' } as const;

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <svg
        viewBox={`0 0 ${LARGURA} ${ALTURA}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={descricao}
        style={{ width: '100%', height: ALTURA, display: 'block' }}
      >
        <defs>
          <linearGradient id={idGradiente} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" style={{ stopColor: 'var(--perfil-1)' }} stopOpacity="0.14" />
            <stop offset="1" style={{ stopColor: 'var(--perfil-1)' }} stopOpacity="0" />
          </linearGradient>
          <clipPath id={idMascara}>
            <rect x="0" y="0" width={LARGURA} height={ALTURA} className={desenhar ? 'painel-desenho' : undefined} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${idMascara})`}>
          {caminhosDeArea(posicoes).map((d) => (
            <path key={d} d={d} fill={`url(#${idGradiente})`} stroke="none" />
          ))}
          <path data-trecho="solido" d={caminhoSolido(posicoes, hojeParcial)} fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={estiloTraco} />
          {tracejado !== null && (
            <path data-trecho="tracejado" d={tracejado} fill="none" strokeWidth="1.5" strokeDasharray="4 3" style={estiloTraco} />
          )}
        </g>
      </svg>
      <div className="flex items-baseline justify-between gap-2" aria-hidden="true">
        <span className={CLASSE_LEGENDA}>{rotulo ?? 'últimos 14 dias úteis'}</span>
        {hojeParcial && <span className={CLASSE_LEGENDA}>hoje (parcial)</span>}
      </div>
    </div>
  );
}
